import { createLogger } from '@derstandard/logger';

const logger = createLogger('authentication');

let AuthJS;

/**
 * load auth api
 */
async function loadAPI() {
	if (AuthJS) {
		return;
	}

	try {
		AuthJS = (await import('__AUTHJS_URL__')).api;
		logger.debug('AuthJS API loaded');

		// expose global auth api for legacy applications, which are not using auth-esm.js yet (e.g. Forum, LiveContent)
		window.DERSTANDARD = window.DERSTANDARD || {};
		if (window.DERSTANDARD) {
			window.DERSTANDARD.auth = { api: AuthJS };
		}
	} catch (err) {
		throw new Error(`AuthJS API could not be loaded - ${err}`);
	}
}

/**
 * Sign in a user
 * @returns {void}
 */
async function signIn(): Promise<void> {
	await loadAPI();
	return await AuthJS.signIn({
		overlayOptions: {
			loginContext: 'leseliste',
			noCloseButton: false,
		},
		triggerType: 'overlay',
	});
}

/**
 * Returns whether the current user is logged in
 * @returns {boolean}
 */
async function userIsLoggedIn(): Promise<boolean> {
	await loadAPI();

	try {
		const session = await AuthJS.getSession();

		return session !== null;
	} catch (error) {
		return false;
	}
}

/**
 *
 * @param {function} callback
 */
async function onStateChanged(callback: (key: Event) => void): Promise<void> {
	await loadAPI();

	if (AuthJS) {
		AuthJS.onStateChanged = async (e: Event) => callback(e);
	}
}

/**
 *
 * @param {function} callback
 */
async function onSignedIn(callback: (key: Event) => void): Promise<void> {
	await loadAPI();

	if (AuthJS) {
		AuthJS.onSignedIn = async (e: Event) => callback(e);
	}
}

/**
 * Returns session token
 * @returns {AuthSession}
 */
async function getSession(): Promise<AuthSession> {
	await loadAPI();
	return await AuthJS.getSession();
}

async function getIdToken(): Promise<string> {
	const session = await getSession();

	if (!session) {
		await signIn();
	}

	return session.idToken.jwtToken;
}

export { getIdToken, getSession, onSignedIn, signIn, onStateChanged, userIsLoggedIn };
